import React, { useState } from 'react';

const GroupMatchResults = ({ groupResults }) => {
  const [expandedTutor, setExpandedTutor] = useState(null);

  if (!groupResults || !groupResults.matches) {
    return null;
  }

  const { group_id, group_size, student_summaries, matches,
          tutors_insufficient_capacity, cost_data, error } = groupResults;

  const getScoreColor = (score) => {
    if (score >= 85) return 'confidence-high';
    if (score >= 70) return 'confidence-medium';
    return 'confidence-low';
  };

  const toggleTutor = (tutorId) => {
    setExpandedTutor(prev => prev === tutorId ? null : tutorId);
  };

  return (
    <div className="group-match-results">
      {/* Header */}
      <div className="group-header">
        <h2>Group Match Results</h2>
        <div className="group-summary">
          <span className="summary-item">
            <strong>{group_size}</strong> students in group
          </span>
          <span className="summary-item">
            <strong>{matches.length}</strong> tutors found
          </span>
          {tutors_insufficient_capacity?.length > 0 && (
            <span className="summary-item warning">
              <strong>{tutors_insufficient_capacity.length}</strong> tutors excluded (capacity)
            </span>
          )}
        </div>
        {cost_data && (
          <div className="group-cost">
            Est. cost: ${cost_data.cost?.toFixed(4) || cost_data.total_cost?.toFixed(4) || '0.00'}
          </div>
        )}
      </div>

      <div className="batch-id-info">
        <small>Group ID: {group_id}</small>
      </div>

      {/* Students in Group */}
      <div className="group-students-preview">
        <h4>Students in this group:</h4>
        <div className="student-chips">
          {student_summaries?.map((s) => (
            <span key={s.uuid} className="student-chip">
              {s.grade} - {s.subjects?.split(',')[0] || 'General'}
            </span>
          ))}
        </div>
      </div>

      {/* Capacity Warnings */}
      {tutors_insufficient_capacity?.length > 0 && (
        <div className="capacity-warning-banner">
          <strong>{tutors_insufficient_capacity.length} tutors excluded</strong>
          {' - insufficient capacity for group of {group_size}'}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="group-error">
          <p>{error}</p>
        </div>
      )}

      {/* Tutor Matches */}
      <div className="group-tutors-list">
        {matches.map((match) => (
          <div
            key={match.tutor_id}
            className={`group-tutor-card ${getScoreColor(match.group_score)}`}
          >
            <div className="tutor-rank">#{match.rank}</div>

            <div className="tutor-main">
              <div className="tutor-header" onClick={() => toggleTutor(match.tutor_id)}>
                {match.tutor_headshot ? (
                  <img src={match.tutor_headshot} alt="" className="tutor-avatar" />
                ) : (
                  <div className="tutor-avatar-placeholder">
                    {match.tutor_name?.charAt(0) || '?'}
                  </div>
                )}
                <div className="tutor-info">
                  <h3>{match.tutor_name}</h3>
                  <div className="group-score">
                    <div className="score-bar">
                      <div
                        className={`score-fill ${getScoreColor(match.group_score)}`}
                        style={{ width: `${match.group_score}%` }}
                      />
                    </div>
                    <span className={`score-value ${getScoreColor(match.group_score)}`}>
                      {match.group_score}% Group Score
                    </span>
                  </div>
                </div>
                <span className={`expand-icon ${expandedTutor === match.tutor_id ? 'expanded' : ''}`}>
                  {expandedTutor === match.tutor_id ? '−' : '+'}
                </span>
              </div>

              <p className="group-explanation">{match.group_explanation}</p>

              {/* Limiting Factor Warning */}
              {match.limiting_factor && (
                <div className="limiting-factor">
                  <span className="warning-icon">⚠️</span>
                  <span>
                    Lowest compatibility: {match.limiting_factor.score}%
                    <small> ({match.limiting_factor.student_uuid?.slice(0, 8)}...)</small>
                  </span>
                </div>
              )}

              {/* Per-Student Details (Expandable) */}
              {expandedTutor === match.tutor_id && (
                <div className="per-student-details">
                  <h4>Per-Student Breakdown</h4>
                  {match.per_student_scores?.map((ps) => (
                    <div key={ps.student_uuid} className="student-score-row">
                      <div className="student-label">
                        <span className="student-grade">{ps.student_grade}</span>
                        <span className="student-subjects">{ps.student_subjects}</span>
                      </div>
                      <div className="student-score-bar">
                        <div
                          className={`bar-fill ${getScoreColor(ps.confidence_score)}`}
                          style={{ width: `${ps.confidence_score}%` }}
                        />
                      </div>
                      <span className={`student-score-value ${getScoreColor(ps.confidence_score)}`}>
                        {ps.confidence_score}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {matches.length === 0 && !error && (
        <div className="no-matches">
          <h3>No tutors available</h3>
          <p>No tutors have sufficient capacity for a group of {group_size} students.</p>
        </div>
      )}
    </div>
  );
};

export default GroupMatchResults;
